import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useToast,
  Spinner,
  Center,
  IconButton,
  Flex,
  useColorMode,
  Badge,
  Select,
} from '@chakra-ui/react';
import { AddIcon, SunIcon, MoonIcon, CloseIcon, RepeatIcon } from '@chakra-ui/icons';
import { useAuth } from '../context/AuthContext';
import DeclarationTabs from '../components/DeclarationTabs';
import DeclarationFormModal from '../components/DeclarationFormModal';
import { declarationsAPI, authAPI, commercialsAPI } from '../utils/api';

const Dashboard = () => {
  const { commercial, userRole, logout } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const [declarations, setDeclarations] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('cheque');
  const [selectedCommercial, setSelectedCommercial] = useState('');
  const toast = useToast();

  useEffect(() => {
    fetchDeclarations();
    if (userRole === 'Technico') {
      fetchCommercials();
    }
  }, [userRole]);

  const fetchDeclarations = async () => {
    try {
      setRefreshing(true);
      const data = await declarationsAPI.getAll();
      setDeclarations(data);
      toast({
        title: 'Succès',
        description: 'Données actualisées',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error fetching declarations:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les déclarations',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const fetchCommercials = async () => {
    try {
      const data = await commercialsAPI.getAll();
      setCommercials(data);
    } catch (error) {
      console.error('Error fetching commercials:', error);
    }
  };

  const handleAddDeclaration = (type) => {
    setSelectedType(type);
    setIsModalOpen(true);
  };

  const handleDeclarationAdded = async (newDeclaration) => {
    // Add a small delay to ensure backend has processed the declaration details
    await new Promise(resolve => setTimeout(resolve, 500));
    // Refetch all declarations to ensure we get the latest data with details
    fetchDeclarations();
    setIsModalOpen(false);
    toast({
      title: 'Succès',
      description: 'Déclaration ajoutée avec succès',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      logout();
      toast({
        title: 'Déconnexion',
        description: 'Vous avez été déconnecté',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Still logout locally even if server logout fails
      logout();
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'Technico': return 'Technicien';
      case 'Commercial': return 'Commercial';
      case 'Caissier': return 'Caissier';
      case 'Serveur': return 'Serveur';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Technico': return 'purple';
      case 'Commercial': return 'blue';
      case 'Caissier': return 'green';
      case 'Serveur': return 'orange';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="brand.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" _dark={{ bg: 'gray.900' }}>
      {/* Header */}
      <Box
        bg="white"
        _dark={{ bg: 'gray.800' }}
        shadow="sm"
        px={4}
        py={4}
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Flex justify="space-between" align="center" maxW="1200px" mx="auto">
          <VStack align="start" spacing={1}>
            <HStack spacing={3}>
              <Heading size="md" color="brand.500">
                Bienvenue, {commercial?.name}
              </Heading>
              <Badge colorScheme={getRoleColor(userRole)} variant="subtle">
                {getRoleLabel(userRole)}
              </Badge>
            </HStack>
            <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
              Gestion des déclarations
            </Text>
          </VStack>
          
          {/* Dark Mode Toggle and Logout Icons */}
          <HStack spacing={2}>
            <IconButton
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              variant="ghost"
              size="md"
              aria-label="Toggle color mode"
            />
            <IconButton
              icon={<RepeatIcon />}
              onClick={fetchDeclarations}
              variant="ghost"
              size="md"
              aria-label="Actualiser les données"
              title="Actualiser les données"
            />
            <IconButton
              icon={<CloseIcon />}
              onClick={handleLogout}
              variant="ghost"
              size="md"
              colorScheme="red"
              aria-label="Se déconnecter"
            />
          </HStack>
        </Flex>
      </Box>

      {/* Main Content */}
      <Box maxW="1200px" mx="auto" px={4} py={6}>
        <VStack spacing={6} align="stretch">
          {/* Role-specific content */}
          {userRole === 'Technico' && (
            <>
            

              {/* Quick Add Buttons for Technico */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={4}>
                  Ajouter une déclaration
                </Text>
                <HStack spacing={3} wrap="wrap">
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('cheque')}
                    colorScheme="blue"
                    size="md"
                  >
                    Chèque
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('effet')}
                    colorScheme="green"
                    size="md"
                  >
                    Effet
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('espece')}
                    colorScheme="orange"
                    size="md"
                  >
                    Espèce
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('virement')}
                    colorScheme="purple"
                    size="md"
                  >
                    Virement
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('versement deplacer')}
                    colorScheme="pink"
                    size="md"
                  >
                    Versement Déplacé
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('note de frais')}
                    colorScheme="yellow"
                    size="md"
                  >
                    Note de Frais
                  </Button>
                </HStack>
              </Box>
            </>
          )}

          {userRole === 'Commercial' && (
            <>
              {/* Quick Add Buttons for Commercial */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={4}>
                  Ajouter une déclaration
                </Text>
                <HStack spacing={3} wrap="wrap">
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('cheque')}
                    colorScheme="blue"
                    size="md"
                  >
                    Chèque
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('effet')}
                    colorScheme="green"
                    size="md"
                  >
                    Effet
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('espece')}
                    colorScheme="orange"
                    size="md"
                  >
                    Espèce
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('virement')}
                    colorScheme="purple"
                    size="md"
                  >
                    Virement
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('compte client')}
                    colorScheme="teal"
                    size="md"
                  >
                    Compte Client
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('versement deplacer')}
                    colorScheme="pink"
                    size="md"
                  >
                    Versement Déplacé
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={() => handleAddDeclaration('note de frais')}
                    colorScheme="yellow"
                    size="md"
                  >
                    Note de Frais
                  </Button>
                </HStack>
              </Box>
            </>
          )}

          {/* Declarations Tabs */}
          <DeclarationTabs 
            declarations={declarations}
            onRefresh={fetchDeclarations}
            userRole={userRole}
          />
        </VStack>
      </Box>

      {/* Add Declaration Modal */}
      <DeclarationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={selectedType}
        onDeclarationAdded={handleDeclarationAdded}
        userRole={userRole}
        selectedCommercial={selectedCommercial}
        commercials={commercials}
      />
    </Box>
  );
};

export default Dashboard; 